import { BaseListStore } from './baseListStore';
import { AbstractBaseListStore } from './abstractBaseListStore';
import { IFaceData } from '@/editor/data/face/face';
import { RegionBaseData, RegionData, RegionExtraData } from '@/editor/data/region/Region';
import { EEntityType } from '@/editor/types';
import { TGlobalStores } from '..';

export class BaseRegionStore implements AbstractBaseListStore<RegionData> {
  private _regionBaseDataMap;
  private _regionExtraDataMap;

  constructor(gs: TGlobalStores) {
    this._regionBaseDataMap = new BaseListStore<RegionBaseData>(gs, {
      dataType: EEntityType.Region,
      onDataChanged: (changedId) => this._syncExtraData(changedId)
    });
    this._regionExtraDataMap = new Map<string, RegionExtraData>();
  }

  /**
   * 让 extra 表跟着 base 表同生同死，理由同 BaseRoomStore._syncExtraData。
   *
   * 分区的壁芯几何只在 extra 里(RegionBaseData 不含 corePathInfos)，占位只能留空；
   * 认领靠的是 base 里的 relateSeparateWallIds ——
   * matchRegionsBySepWalls 第一轮就是按它做 key，且 refreshRegionDatasByRoomId 每次兜底认领后都会把它刷新。
   */
  private _syncExtraData(changedId?: string) {
    // clear / addAll 是整表替换，不带 id，按全量对齐
    if (changedId === undefined) {
      const baseIds = new Set(this._regionBaseDataMap.getList().map((item) => item.id));
      this._regionExtraDataMap.forEach((_, id) => {
        if (!baseIds.has(id)) this._regionExtraDataMap.delete(id);
      });
      baseIds.forEach((id) => this._syncExtraData(id));
      return;
    }

    if (!this._regionBaseDataMap.get(changedId)) {
      this._regionExtraDataMap.delete(changedId);
      return;
    }
    if (this._regionExtraDataMap.has(changedId)) return;

    this._regionExtraDataMap.set(changedId, {
      height: 0,
      pathInfos: [],
      holeInfos: [],
      corePathInfos: [],
      coreHoleInfos: []
    });
  }

  add(data: RegionData) {
    this._regionBaseDataMap.add({
      id: data.id,
      offGround: data.offGround,
      relateSeparateWallIds: data.relateSeparateWallIds,
      regionInfo: data.regionInfo,
      relateRoomId: data.relateRoomId,
      materialInfo: data.materialInfo
    });
    this._regionExtraDataMap.set(data.id, {
      height: data.height,
      pathInfos: data.pathInfos,
      holeInfos: data.holeInfos,
      corePathInfos: data.corePathInfos,
      coreHoleInfos: data.coreHoleInfos
    });
  }

  update(_id: string, _data: IFaceData) {
    console.warn('can not update region data directly');
  }

  updateBaseData(id: string, data: Partial<RegionBaseData>) {
    if (!id || !data) return;
    const currentData = this._regionBaseDataMap.get(id);
    if (!currentData) return;
    this._regionBaseDataMap.update(id, data);
  }

  updateExtraData(id: string, data: RegionExtraData) {
    if (!id || !data) return;
    this._regionExtraDataMap.set(id, data);
  }

  remove(id: string) {
    if (!id || !this._regionBaseDataMap.get(id)) return;

    this._regionBaseDataMap.remove(id);
    this._regionExtraDataMap.delete(id);
  }

  get(id: string) {
    const baseData = this._regionBaseDataMap.get(id);
    if (!baseData) return;
    // _syncExtraData 保证 base 在则 extra 在，取不到属于程序错误，按取不到处理不再往下拼半份数据
    const extraData = this._regionExtraDataMap.get(id);
    if (!extraData) return;

    return {
      ...baseData,
      ...extraData
    };
  }

  getList() {
    return this._regionBaseDataMap.getList().flatMap((item) => {
      const extraData = this._regionExtraDataMap.get(item.id);
      return extraData ? [{ ...item, ...extraData }] : [];
    });
  }

  clear() {
    this._regionBaseDataMap.clear();
    this._regionExtraDataMap.clear();
  }
}
